import { useState } from 'react';
import PropTypes from 'prop-types';

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-200">
    <button
      onClick={onClick}
      className="w-full py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
    >
      <span className="font-medium text-gray-900">{question}</span>
      <svg
        className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && (
      <div className="pb-4 text-gray-600 text-sm leading-relaxed">
        {answer}
      </div>
    )}
  </div>
);

FAQItem.propTypes = {
  question: PropTypes.string.isRequired,
  answer: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

const HelpPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      question: "What is Kwikly?",
      answer: "Kwikly is an AI-powered job application tool that helps you tailor your resume for specific job postings in seconds. It captures jobs from LinkedIn, analyzes the job description, and optimizes your resume to beat Applicant Tracking Systems (ATS) with a higher match score."
    },
    {
      question: "How does the Chrome extension work?",
      answer: (
        <>
          <ol className="list-decimal ml-4 space-y-2">
            <li>Install the Kwikly extension from the Chrome Web Store</li>
            <li>Browse LinkedIn Jobs as you normally would</li>
            <li>When you see a job you like, click the &quot;Save to Kwikly&quot; button that appears</li>
            <li>The job details are automatically captured and saved to your Kwikly dashboard</li>
            <li>Click &quot;Tailor Resume&quot; to generate an optimized version for that specific job</li>
          </ol>
        </>
      )
    },
    {
      question: "What is an ATS score?",
      answer: "ATS (Applicant Tracking System) score measures how well your resume matches a job description. Most companies use ATS software to filter resumes before a human sees them. A higher score (90%+) means your resume is more likely to pass through these filters. Kwikly analyzes keywords, skills, and formatting to maximize your score."
    },
    {
      question: "Is my data secure?",
      answer: (
        <>
          Yes, we take data security seriously:
          <ul className="list-disc ml-4 mt-2 space-y-1">
            <li>Your resume data is encrypted at rest and in transit</li>
            <li>We never share your personal information with third parties</li>
            <li>You can delete your data at any time from Settings</li>
            <li>We comply with GDPR, CCPA, and India&apos;s DPDP Act</li>
          </ul>
          <a href="/privacy" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">Read our full Privacy Policy →</a>
        </>
      )
    },
    {
      question: "What's included in the Free plan?",
      answer: (
        <>
          The Free plan includes:
          <ul className="list-disc ml-4 mt-2 space-y-1">
            <li>Save up to 3 jobs per week</li>
            <li>Tailor up to 2 resumes per month</li>
            <li>Basic ATS score analysis</li>
            <li>PDF downloads (with Kwikly watermark)</li>
          </ul>
        </>
      )
    },
    {
      question: "What's included in the Pro plan?",
      answer: (
        <>
          Pro plan ($19/month or $190/year) includes:
          <ul className="list-disc ml-4 mt-2 space-y-1">
            <li>Unlimited job saves</li>
            <li>Unlimited resume tailoring</li>
            <li>Advanced ATS analysis with keyword breakdown</li>
            <li>Clean PDF downloads (no watermark)</li>
            <li>Cover letter generation</li>
            <li>Priority support</li>
          </ul>
        </>
      )
    },
    {
      question: "How do I cancel my subscription?",
      answer: "You can cancel anytime from the Settings page in your dashboard. Go to Settings → Billing → Cancel Subscription. Your Pro features will remain active until the end of your billing period. No refunds for partial months."
    },
    {
      question: "The extension isn't working on LinkedIn. What should I do?",
      answer: (
        <>
          Try these troubleshooting steps:
          <ol className="list-decimal ml-4 mt-2 space-y-1">
            <li>Make sure you&apos;re on a LinkedIn job detail page (not just the search results)</li>
            <li>Refresh the page after installing the extension</li>
            <li>Check that you&apos;re logged into Kwikly (click the extension icon to verify)</li>
            <li>Try disabling other LinkedIn extensions temporarily</li>
            <li>Clear your browser cache and restart Chrome</li>
          </ol>
          <p className="mt-2">Still having issues? <a href="/support" className="text-primary-600 hover:text-primary-700">Contact support</a></p>
        </>
      )
    },
    {
      question: "How do referrals work?",
      answer: "When you share your referral link and someone signs up, both you and your friend get 7 free days of Pro access. There's no limit to how many people you can refer. Find your referral link in Settings → Referrals."
    },
    {
      question: "Can I use Kwikly on job sites other than LinkedIn?",
      answer: "Currently, our Chrome extension supports LinkedIn Jobs. Support for Indeed, Glassdoor, and other job boards is coming soon. You can also paste any job description manually on our web app to tailor your resume."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <a href="/" className="inline-block mb-6">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mx-auto shadow-lg">
              <span className="text-primary-600 font-bold text-xl">K</span>
            </div>
          </a>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Help Center
          </h1>
          <p className="text-primary-100 text-lg max-w-2xl mx-auto">
            Everything you need to know about using Kwikly
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Quick Links */}
        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          <a href="#getting-started" className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900">Getting Started</h3>
            <p className="text-gray-500 text-sm mt-1">Learn the basics</p>
          </a>

          <a href="#extension" className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900">Chrome Extension</h3>
            <p className="text-gray-500 text-sm mt-1">Installation & usage</p>
          </a>

          <a href="/support" className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900">Contact Support</h3>
            <p className="text-gray-500 text-sm mt-1">Get personal help</p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="divide-y divide-gray-200">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* Still need help */}
        <div className="mt-8 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-8 text-center text-white">
          <h3 className="text-xl font-semibold mb-2">Still have questions?</h3>
          <p className="text-primary-100 mb-4">Our support team is here to help</p>
          <a
            href="/support"
            className="inline-block px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Kwikly. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <a href="/" className="hover:text-gray-700">Home</a>
            <a href="/privacy" className="hover:text-gray-700">Privacy</a>
            <a href="/support" className="hover:text-gray-700">Support</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
